import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
    selectBuilding,
    selectDevice,
    selectFeedback,
    selectRoom,
    selectSensor,
    selectUserData,
} from "../../features/store";
import PopUp from "../../components/PopUp";
import { FaTimesCircle, FaChartPie, FaRegChartBar } from "react-icons/fa";
import { BsArrowLeftCircle } from "react-icons/bs";
import { IoRefreshCircleOutline } from "react-icons/io5";
import { Chip, InfoBlock } from "../../components/General";
import { getDisplayTime } from "../../utils/General";
import { getCurrentCount, getCumulativeCount } from "../../apis/PeopleCount.Api";
import { PieChart, Pie, ResponsiveContainer, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis } from "recharts";
import { Slide } from "@mui/material";
import { useSnackbar } from "notistack";
import moment from "moment";

const colorConverter = {
    Normal: "#38bf8b",
    Alert: "#ffca3a",
    Offline: "#ff595e",
};
const nameConverter = {
    Normal: "Normal",
    Alert: "Alert",
    Offline: "Offline",
};

const RoomInfo = ({ currBuilding }) => {
    const buildings = useSelector(selectBuilding);
    const rooms = useSelector(selectRoom);
    const sensors = useSelector(selectSensor);
    const devices = useSelector(selectDevice);
    const [selectedRoom, setSelectedRoom] = useState({});
    const [displayRoomDict, setDisplayRoomDict] = useState({});
    async function getInitialRoomDict() {
        let filteredRoomDict = {};
        for (let rid of Object.keys(rooms).sort((rid1, rid2) => {
            return rooms[rid1].roomName.localeCompare(rooms[rid2].roomName, undefined, { sensitivity: "base" });
        })) {
            const roomDevices = Object.values(devices).filter((device) => device.roomId === rid);
            if (rooms[rid].buildingId !== currBuilding.buildingId) continue;
            let roomStatus = "Normal";
            if (roomDevices.length > 0) {
                const hasOffline = roomDevices.some((d) => d.status === "Offline" || !d.status);
                const hasAlert = roomDevices.some((d) => d.status === "Alert");

                if (hasOffline && hasAlert) {
                    roomStatus = "Half";
                } else if (hasOffline) {
                    roomStatus = "Offline";
                } else if (hasAlert) {
                    roomStatus = "Alert";
                } else {
                    roomStatus = roomDevices[0].status;
                }
            }

            filteredRoomDict[rid] = {
                ...rooms[rid],
                status: roomStatus,
                alertSensor: 0,
                offlineSensor: 0,
                sensors: [],
                devices: [],
                currentUser: displayRoomDict[rid] ? displayRoomDict[rid].currentUser : "--",
                cumulativeUser: displayRoomDict[rid] ? displayRoomDict[rid].cumulativeUser : "--",
                lastCountUpdate: displayRoomDict[rid] ? displayRoomDict[rid].lastCountUpdate : "--",
            };
            // add nano of the room to device list
            if (rooms[rid].nanoId) {
                filteredRoomDict[rid].devices.push(devices[rooms[rid].nanoId]);
            }
        }

        // Get Sensor Info
        for (let sid of Object.keys(sensors)) {
            let sensor = sensors[sid];
            let rid = sensor.roomId;
            if (rooms[rid].buildingId !== currBuilding.buildingId) continue;
            filteredRoomDict[rid].sensors.push(sensor);
            let stat = filteredRoomDict[rid].status;
            let thisStat = sensor.status;

            if (!thisStat) {
                if (stat === "Alert") {
                    filteredRoomDict[rid].status = "Half";
                } else if (stat === "Normal") {
                    filteredRoomDict[rid].status = "Offline";
                } else if (stat === "Offline") {
                    filteredRoomDict[rid].status = "Offline";
                }
            } else if (thisStat === "Offline") {
                filteredRoomDict[rid].offlineSensor += 1;
                if (stat === "Alert") {
                    filteredRoomDict[rid].status = "Half";
                } else if (stat === "Normal") {
                    filteredRoomDict[rid].status = "Offline";
                }
            } else if (thisStat === "Alert") {
                filteredRoomDict[rid].alertSensor += 1;
                if (stat === "Normal") {
                    filteredRoomDict[rid].status = "Alert";
                }
            } else if (thisStat === "Half") {
                filteredRoomDict[rid].status = "Half";
            }
        }

        // Get Device Info (Feedback, Footfall)
        for (let did of Object.keys(devices)) {
            let device = devices[did];
            let rid = device.roomId;
            if (!rid) continue;
            if (rooms[rid].buildingId !== currBuilding.buildingId) continue;

            let stat = filteredRoomDict[rid].status;
            let thisStat = device.status;

            if (!thisStat) {
                if (stat === "Alert") {
                    filteredRoomDict[rid].status = "Half";
                } else if (stat === "Normal") {
                    filteredRoomDict[rid].status = "Offline";
                } else if (stat === "Offline") {
                    filteredRoomDict[rid].status = "Offline";
                }
            } else if (thisStat === "Offline") {
                filteredRoomDict[rid].offlineSensor += 1;
                if (stat === "Alert") {
                    filteredRoomDict[rid].status = "Half";
                } else if (stat === "Normal") {
                    filteredRoomDict[rid].status = "Offline";
                }
            } else if (thisStat === "Alert") {
                filteredRoomDict[rid].alertSensor += 1;
                if (stat === "Normal") {
                    filteredRoomDict[rid].status = "Alert";
                }
            } else if (thisStat === "Half") {
                filteredRoomDict[rid].status = "Half";
            }
            filteredRoomDict[rid].devices.push(device);
        }

        setDisplayRoomDict(filteredRoomDict);
    }
    useEffect(() => {
        getInitialRoomDict();
    }, [currBuilding, buildings, rooms, sensors, devices]);
    useEffect(() => {
        if (Object.keys(selectedRoom).length === 0) return;
        if (Object.keys(displayRoomDict).length === 0) return;
        let currRid = selectedRoom.roomId;
        let newRoom = displayRoomDict[currRid];
        if (JSON.stringify(selectedRoom) !== JSON.stringify(newRoom)) {
            setSelectedRoom(displayRoomDict[currRid]);
        }
    }, [displayRoomDict]);
    return (
        <div className="room-info">
            <RoomInfoBlock displayRoomDict={displayRoomDict} setSelectedRoom={setSelectedRoom} />
            <TableFooter />
            <RoomInfoPopUp selectedRoom={selectedRoom} setSelectedRoom={setSelectedRoom} />
        </div>
    );
};

export default RoomInfo;

const RoomInfoBlock = ({ displayRoomDict, setSelectedRoom }) => {
    return (
        <div className="room-info-block-wrapper">
            {Object.keys(displayRoomDict).length ? (
                <div className="room-info-block">
                    {Object.keys(displayRoomDict).map((rid, index) => {
                        let element = displayRoomDict[rid];

                        let nanoData = {};
                        if (Array.isArray(element.devices)) {
                            for (let device of element.devices) {
                                if (device.deviceId.startsWith("N")) {
                                    nanoData = device;
                                    break;
                                }
                            }
                        }

                        return (
                            <Chip
                                key={index + "sensorButton"}
                                status={element.status}
                                text={element.roomName}
                                nanoData={nanoData}
                                onClick={() => {
                                    setSelectedRoom(element);
                                }}
                            />
                        );
                    })}
                </div>
            ) : (
                <p className="empty-bld"> No room found in this building</p>
            )}
        </div>
    );
};

const TableFooter = () => {
    return (
        <div className="footer">
            {["Normal", "Alert", "Offline", "Null"].map((e) => (
                <div className="footerBlock" key={e}>
                    <div className="statusCircle" style={{ backgroundColor: colorConverter[e] }}></div>
                    <p>{nameConverter[e]}</p>
                </div>
            ))}
        </div>
    );
};
const RoomInfoPopUp = ({ selectedRoom, setSelectedRoom }) => {
    const [selectedSensor, setSelectedSensor] = useState(null);
    const userData = useSelector(selectUserData);
    const feedback = useSelector(selectFeedback);

    const [currentCount, setCurrentCount] = useState("--");
    const [cumulativeCount, setCumulativeCount] = useState("--");
    const [lastCountUpdate, setLastCountUpdate] = useState("--");
    const [displayChart, setDisplayChart] = useState([]);
    const containerRef = useRef(null);
    const { enqueueSnackbar } = useSnackbar();
    const [chartType, setChartType] = useState("bar");
    const [refresh, setRefresh] = useState(1);

    useEffect(() => {
        initRoomInfo();
        if (selectedSensor) {
            let currSid = selectedSensor.sensorId;
            for (let s of selectedRoom.sensors) {
                if (s.sensorId === currSid) {
                    setSelectedSensor(s);
                }
            }
        }
    }, [selectedRoom, feedback, refresh]);
    function refreshData() {
        let r = -refresh;
        setRefresh(r);
    }
    async function initRoomInfo() {
        setCurrentCount("--");
        setCumulativeCount("--");
        setLastCountUpdate("--");
        setDisplayChart([]);
        if (Object.keys(selectedRoom).length === 0) return;

        let resCurr = await getCurrentCount({
            room_id: JSON.stringify([selectedRoom.roomId]),
        });
        if (!resCurr.data.indicator) {
            enqueueSnackbar(resCurr.data.message, { variant: "error" });
            return;
        }
        if (resCurr.data.message.length !== 0) {
            let currentUser = resCurr.data.message[0].current_count;
            let updateTime = getDisplayTime(resCurr.data.message[0].update_time);

            setCurrentCount(currentUser);
            setLastCountUpdate(updateTime);
        }

        let start = new Date();
        start.setHours(0, 0, 0, 0);

        let res = await getCumulativeCount({
            room_id: selectedRoom.roomId ? JSON.stringify([selectedRoom.roomId]) : null,
            start_time: moment(start).format("YYMMDDHHmmssSSS"),
        });
        if (!res.data.indicator) {
            enqueueSnackbar(res.data.message, { variant: "error" });
            return;
        }
        if (res.data.message.length !== 0) {
            let cumulative = res.data.message[0].cumulative_count;
            setCumulativeCount(cumulative);
        }
        // Init Feedback
        let chartData = [
            { name: "Wet Floor", value: 0 },
            { name: "Ammonia level High", value: 0 },
            { name: "No Paper Roll", value: 0 },
            { name: "No Hand Soap", value: 0 },
        ];
        const converter = {
            "Wet Floor": 0,
            "Ammonia level High": 1,
            "No Paper Roll": 2,
            "No Hand Soap": 3,
        };
        let rid = selectedRoom.roomId;

        if (feedback[rid]) {
            for (let f of feedback[rid]) {
                if (f.message === "") continue;
                chartData[converter[f.message]].value += 1;
            }
        }

        setDisplayChart(chartData);
    }

    return (
        <PopUp width={65} height={90} switchOn={Object.keys(selectedRoom).length !== 0}>
            <div className="pop-up-content-wrapper">
                <div className="pop-up-row">
                    <h4>{selectedRoom.roomName}</h4>

                    <FaTimesCircle
                        className="pop-up-close-btn"
                        onClick={() => {
                            setSelectedRoom({});
                            setSelectedSensor(null);
                        }}
                    />
                </div>
                <div className="pop-up-division-line"></div>
                <div className="pop-up-body">
                    <div className="room-info-pop-up-content" ref={containerRef}>
                        <Slide
                            direction="right"
                            in={!selectedSensor}
                            mountOnEnter
                            unmountOnExit
                            container={containerRef.current}
                        >
                            <div className="slider-wrapper">
                                <div className="refresh-group">
                                    <p>Click to refresh data:</p>
                                    <IoRefreshCircleOutline
                                        onClick={() => {
                                            refreshData();
                                        }}
                                        size={"1.3rem"}
                                        className="refresh-icon"
                                    />
                                </div>

                                <div className="info-blocks">
                                    {Object.entries({
                                        Floor: selectedRoom["floor"],
                                        "Current User": currentCount,
                                        "Cumulative User (Today)": cumulativeCount,
                                        "Last Count Update": lastCountUpdate,
                                    }).map((entry) => {
                                        return (
                                            <InfoBlock key={entry[0] + "infoBlock"} title={entry[0]} value={entry[1]} />
                                        );
                                    })}
                                </div>
                                <div className="feedback-block">
                                    <InfoBlock title={`Feedback (Today)`}>
                                        <div className="complaint-chart-wrapper">
                                            <div
                                                className="swap-chart-icon"
                                                onClick={() => {
                                                    if (chartType === "bar") {
                                                        setChartType("pie");
                                                    } else if (chartType === "pie") {
                                                        setChartType("bar");
                                                    }
                                                }}
                                            >
                                                {chartType === "bar" ? <FaChartPie /> : <FaRegChartBar />}
                                            </div>
                                            {chartType === "bar" ? (
                                                <ComplaintBarChart data={displayChart} xKey={"name"} />
                                            ) : (
                                                <ComplaintPieChart data={displayChart} />
                                            )}
                                        </div>
                                    </InfoBlock>
                                </div>

                                <div className="sensor-blocks">
                                    <div className="sensor-blocks-header">
                                        <p> Sensors & devices </p>
                                        <div className="sensor-blocks-legends">
                                            {["Normal", "Alert", "Offline"].map((e) => (
                                                <div className="sensor-blocks-legend" key={e}>
                                                    <div
                                                        className="statusCircle"
                                                        style={{ backgroundColor: colorConverter[e] }}
                                                    ></div>
                                                    <p> = {nameConverter[e]}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    {["Ammonia", "Paper Roll", "Hand Towel", "Soap", "Devices"].map((e) => (
                                        <SensorBlock
                                            selectedRoom={selectedRoom}
                                            type={e}
                                            setSelectedSensor={setSelectedSensor}
                                            key={e}
                                        />
                                    ))}
                                </div>
                            </div>
                        </Slide>
                        <Slide
                            direction="left"
                            in={selectedSensor !== null}
                            mountOnEnter
                            unmountOnExit
                            container={containerRef.current}
                        >
                            <div className="slider-wrapper">
                                <div className="sensor-detail">
                                    <div className="sensor-detail-top">
                                        <BsArrowLeftCircle
                                            onClick={() => {
                                                setSelectedSensor(null);
                                            }}
                                            size={"20px"}
                                            className="slide-back-btn"
                                        />
                                        <p className="sensor-name">{selectedSensor && selectedSensor.sensorName}</p>
                                        <p className="sensor-name">
                                            {selectedSensor && !selectedSensor.sensorName && selectedSensor.deviceId}
                                        </p>
                                    </div>

                                    <div className="info-blocks">
                                        {selectedSensor &&
                                            Object.keys(selectedSensor)
                                                .filter((key) =>
                                                    [
                                                        "sensorId",
                                                        "deviceId",
                                                        "sensorType",
                                                        "status",
                                                        "updateTime",
                                                    ].includes(key)
                                                )
                                                .map((key, i) => {
                                                    let display = selectedSensor[key];
                                                    let titles = {
                                                        sensorId: "Sensor ID",
                                                        deviceId: "Device ID",
                                                        sensorType: "Type",
                                                        status: "Status",
                                                        updateTime: "Last Updated",
                                                    };
                                                    if (key === "updateTime") {
                                                        display = getDisplayTime(display);
                                                    }
                                                    return (
                                                        <InfoBlock
                                                            key={"sensorInfo" + i}
                                                            title={titles[key]}
                                                            value={display}
                                                        />
                                                    );
                                                })}
                                    </div>
                                </div>
                            </div>
                        </Slide>
                    </div>
                </div>
            </div>
        </PopUp>
    );
};

const SensorBlock = ({ selectedRoom, setSelectedSensor, type }) => {
    return (
        <div className="sensor-block">
            <p className="sensor-type">{type}</p>
            <div className="sensors">
                {type !== "Devices" &&
                    selectedRoom.sensors &&
                    selectedRoom.sensors
                        .filter((element) => element.sensorType === type)
                        .map((element, index) => {
                            return (
                                <Chip
                                    key={index + "sensorButton"}
                                    status={element.status}
                                    text={element.sensorName}
                                    onClick={() => {
                                        setSelectedSensor(element);
                                    }}
                                ></Chip>
                            );
                        })}
                {type === "Devices" &&
                    selectedRoom.devices &&
                    selectedRoom.devices.map((element, index) => {
                        return (
                            <Chip
                                key={element.deviceId + "deviceButton"}
                                status={element.status}
                                text={element.deviceId}
                                onClick={() => {
                                    setSelectedSensor(element);
                                }}
                            />
                        );
                    })}
            </div>
        </div>
    );
};
const colors = ["#574477", "#b34a80", "#f7625a", "#ffa600"];

const ComplaintPieChart = ({ data }) => {
    const [noData, setNoData] = useState(true);
    useEffect(() => {
        setNoData(true);
        for (let obj of data) {
            if (obj.value !== 0) {
                setNoData(false);
                break;
            }
        }
    }, [data]);
    return (
        <>
            {noData ? (
                <>
                    <p className="no-complaint">No complaints today</p>
                </>
            ) : (
                <ResponsiveContainer width={"100%"} height={200}>
                    <PieChart>
                        <Pie
                            dataKey="value"
                            isAnimationActive={true}
                            data={data}
                            cx="50%"
                            cy="45%"
                            outerRadius={"90%"}
                            fill="#8884d8"
                        >
                            {[0, 1, 2, 3].map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                            ))}
                        </Pie>
                        <Legend />
                        <Tooltip />
                    </PieChart>
                </ResponsiveContainer>
            )}
        </>
    );
};

const ComplaintBarChart = ({ data, xKey }) => {
    const [noData, setNoData] = useState(true);
    useEffect(() => {
        setNoData(true);
        for (let obj of data) {
            if (obj.value !== 0) {
                setNoData(false);
                break;
            }
        }
    }, [data]);

    return (
        <>
            {noData ? (
                <>
                    <p className="no-complaint">No complaints today</p>
                </>
            ) : (
                <div className="barchart-with-legend">
                    <ResponsiveContainer width={"95%"} height={200}>
                        <BarChart data={data} isAnimationActive>
                            <YAxis type="number" allowDecimals={false} />
                            <XAxis yAxisId={0} dataKey={xKey} type="category" interval={"equidistantPreserveStart"} />
                            <Tooltip />

                            <Bar dataKey={"value"} barSize={30}>
                                {[0, 1, 2, 3].map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                    <div className="custom-legend">
                        {["Wet Floor", "Ammonia level High", "No Paper Roll", "No Hand Soap"].map((text, index) => (
                            <div className="custom-legend-component" key={text}>
                                <div
                                    className="square"
                                    style={{ backgroundColor: colors[index % colors.length] }}
                                ></div>
                                <p style={{ color: colors[index % colors.length] }}>{text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};
